<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import BFlashBaner from 'src/shared/components/base/BFlashBaner/BFlashBaner.vue';
import SettingsPlanTable from 'src/modules/Setting/components/SettingsPlanTable/SettingsPlanTable.vue';
import SettingsOrderCard from 'src/modules/Setting/components/SettingsOrderCard/SettingsOrderCard.vue';
import type { SelectedPlan } from './usePlan';
import { usePlan } from './usePlan';
import type { Plan } from './usePlan';
import {
  useStrapiGet,
  useStrapiPost,
} from 'src/shared/composables/useStrapiApi';
import type { Child } from 'src/modules/Child/models/Child';
import { useQuasar } from 'quasar';
import { t } from 'src/boot/i18n';
// Create the child repository constant

export interface License {
  id: number;
  documentId: string;
  assigned_at: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string;
  locale: string | null;
  valid_until: string;
  child: Child | null;
}

export interface Order {
  id: number;
  documentId: string;
  order_id: string;
  product_id: number;
  action: string;
  action_status: string;
  product_category: string;
  amount: number;
  currency: string;
  createdAt: string;
  licenses: License[];
  child_id: number;
}
const $q = useQuasar();

const { plans, columns } = usePlan();

const selectedPlan = ref<SelectedPlan>({ planId: 0, period: 'month' });

const {
  data: orderDataResponse,
  execute: getOrderData,
  isFinished: isOrderFinished,
} = useStrapiGet('liqpay-plugin/order/me', { 'only-active': true });

onMounted(() => {
  void getOrderData();
});

const orderData = computed<Order[]>(() => {
  return orderDataResponse.value || [];
});

const {
  execute: updateSubscription,
  error: updateError,
  data: updateData,
} = useStrapiPost('liqpay-plugin/pay/update_subscription', {});

async function handleUnsubscribe(orderId: string) {
  // Logic to handle unsubscription
  console.log('Unsubscribe order:', orderId);
  await updateSubscription({
    data: {
      order_id: orderId,
      action: 'unsubscribe',
    },
  });

  if (updateError.value) {
    console.error('Error unsubscribing:', updateError.value);
  }

  if (updateData.value && updateData.value.status === 'success') {
    console.log('Unsubscribe successful:', updateData.value);
    $q.notify({
      type: 'positive',
      message: t('UNSUBSCRIBED_SUCCESSFULLY'),
    });

    // Remove the order by filtering it out
    const index = orderData.value.findIndex((o) => o.order_id === orderId);
    if (index !== -1) {
      orderData.value.splice(index, 1);
    }
  } else {
    console.error('Unsubscribe failed:', updateData.value);
    $q.notify({
      type: 'negative',
      message: t('UNSUBSCRIBE_FAILED'),
    });
  }
}
</script>

<template>
  <section
    class="column no-wrap items-center justify-top q-px-sm scroll fit"
    style="max-height: 100svh"
  >
    <BFlashBaner
      dark
      class="bg-transparent full-width text-center"
      :font-clamp="{ min: 1.125, size: 1.75, max: 2.5 }"
    />

    <BFlashBaner
      dark
      class="bg-transparent text-secondary q-mt-xl full-width text-center"
      :font-clamp="{ min: 2.5, size: 2.75, max: 3.5 }"
    >
      {{ $t('CHOOSE_A_PLAN') }}
    </BFlashBaner>

    <p
      class="text-white q-px-md q-mt-md text-center text-body1"
      style="font-size: clamp(1.25rem, 1.5vw, 1.75rem); max-width: 600px"
    >
      {{ $t('TO_GET_A')
      }}<span class="text-secondary q-mx-xs">{{ $t('FREE_TRIAL') }}</span
      >{{ $t('FOR_A_WEEK_PLEASE_FILL_IN_THE_PAYMENT_INFORMATION') }}
    </p>

    <SettingsPlanTable
      v-if="plans?.data"
      v-model:selected-plan="selectedPlan"
      :plans="plans?.data"
      :columns="columns"
      square
      dark
      flat
      table-class="billing-plan-table"
      class="bg-transparent text-bright q-mt-xl"
    />

    <template v-if="isOrderFinished && orderData">
      <h6 class="text-h6 text-info">
        {{ $t('ACTIVE_SUBSCRIPTIONS') }}
      </h6>
      <SettingsOrderCard
        v-for="order in orderData"
        :key="order.id"
        :order="order"
        :plan="plans?.data.find((p: Plan) => p.id === order.product_id)"
        flat
        dense
        dark
        class="bg-dark"
        square
        @order:unsubscribe="handleUnsubscribe"
      />
    </template>
  </section>
</template>

<style lang="scss">
.billing-plan-table .q-table {
  border-spacing: 0 10px;
}

.billing-plan-table .q-table td {
  border-top: 1px solid #6a6a6a;
  border-bottom: 1px solid #6a6a6a;
}

.billing-plan-table .q-table td:last-child {
  border-inline-end: 1px solid #6a6a6a;
  padding-inline-start: 0;
}
</style>
